import React from 'react';
import { createFragmentContainer, graphql, QueryRenderer } from 'react-relay';

import { LeftNav } from '@onehope/app-shell';
import { LDFlagSet } from 'launchdarkly-js-sdk-common';

import { ROLES } from '../../../../server/config';
import { getCategory } from '../../App/AppHelpers';
import { customPCPConfig } from '../../../../server/utils/customPCPConfig';
import {
  urlCeHome,
  urlProductCatalogPage,
  urlTastingFlightLandingPage,
  urlHopecommerce,
} from '../../../../server/utils/routeConstants';
import { resetSegment } from '../../../utils/tracking';
import reassignLocation from '../../../utils/reassignLocation';
import {
  q32020Config,
  q12021Config,
  createAccountMenuConfig,
} from '../../../utils/leftNavConfig';

type LeftNavProps = {
  handleRouteChange: (
    e: any,
    value: string,
    category: string | null,
    customRoute: string | null,
  ) => void;
  handleRouteStaticRoute: (
    value: string,
    category?: string | null,
    custom?: boolean,
  ) => void;
  handleLogout: (fn: () => void) => void;
  handleShopClose: () => void;
  shopNavOpen: boolean;
  viewer: any;
  // eslint-disable-next-line no-restricted-globals
  location: any;
  environment: any;
  flags: LDFlagSet;
  containerWidth: number;
};

function LeftNavComponent(props: LeftNavProps) {
  const {
    handleRouteChange,
    handleRouteStaticRoute,
    handleLogout,
    handleShopClose,
    shopNavOpen,
    viewer,
    location,
    flags,
    containerWidth,
  } = props;
  if (!props.viewer || !props.viewer.user) {
    return <div />;
  }

  const userLogout = () => {
    const onCompleted = () => {
      resetSegment();
    };
    return handleLogout(onCompleted);
  };

  const getRoute = (value: string) => {
    if (['july', 'august'].some(v => value.includes(v))) {
      const route = value.match(/([^\/]*)\/*$/)[1] || '';
      return `/wine/virtual/${route}`;
    }
    return value;
  };

  const handleMenuLinkSelection = (value: string) => {
    if (!value) return;
    if (value === 'signout') {
      userLogout();
    } else if (value === urlCeHome) {
      // Open CE Dash in same window even though it is a static route
      reassignLocation(value, handleRouteChange);
    } else if (value.includes('https://')) {
      window.location.href = value;
    } else if (
      (value === '/' ||
        Array.isArray(value.match('wine')) ||
        Array.isArray(value.match('gifts')) ||
        Array.isArray(value.match('sale')) ||
        Array.isArray(value.match('swag'))) &&
      value !== '/winemaking'
    ) {
      const route = getRoute(value);
      window.location.href = `${urlProductCatalogPage}${route}`;
    } else if (Array.isArray(value.match('tasting-flight'))) {
      window.location.href = `${urlTastingFlightLandingPage}`;
    } else if (value.includes('#')) {
      handleRouteStaticRoute(value, null, true);
    } else {
      handleRouteStaticRoute(value);
    }
    handleShopClose();
  };

  const isLoggedIn = viewer.user.role !== ROLES.anonymous;
  const isProspect =
    (viewer.user.segmentPersonas &&
      viewer.user.segmentPersonas.prospects === true) ||
    !isLoggedIn;

  const accountConfig = createAccountMenuConfig({
    flags,
    isLoggedIn,
    user: viewer.user,
  });

  const isMobile = containerWidth < 768;
  return (
    <LeftNav
      onClose={handleShopClose}
      openDrawer={shopNavOpen}
      navigate={handleMenuLinkSelection}
      selectedItem={isMobile ? '' : location}
      accountConfig={accountConfig}
      config={q32020Config(isProspect)}
      hopecommerceUrl={urlHopecommerce}
    />
  );
}

const LeftNavFragmentComponent = createFragmentContainer(LeftNavComponent, {
  viewer: graphql`
    fragment LeftNav_viewer on Viewer {
      user {
        id
        id
        email
        firstName
        lastName
        role
        accountRole {
          accountRoleTitle
        }
        segmentPersonas {
          prospects
        }
        permissions {
          id
          oneCommerce {
            id
            ceo {
              access
            }
            admin {
              access
            }
          }
        }
      }
    }
  `,
});

const noVariables = {};

// eslint-disable-next-line react/display-name
export default (props: LeftNavProps) => {
  return (
    <QueryRenderer
      variables={noVariables}
      environment={props.environment}
      query={graphql`
        query LeftNavComponentQuery {
          viewer {
            id
            id
            ...LeftNav_viewer
          }
        }
      `}
      render={({ error, props: propsFromRender }: any) => {
        if (error) {
          return <div />;
        }
        if (propsFromRender && propsFromRender.viewer) {
          return (
            <LeftNavFragmentComponent
              {...props}
              viewer={propsFromRender.viewer}
            />
          );
        }
        return <div />;
      }}
    />
  );
};
